
import { Component, Vue } from "vue-property-decorator";
import { InjectService } from "@/modules/shared/decorators/inject-service.decorator";
import { FindIdentityProviderByIdResponse, ProviderData } from "@flosum-auth/contracts";
import { AbortResponseError } from "@/modules/shared/errors/abort-response.error";
import AppButton from "@/modules/shared/components/AppButton.vue";
import AppSkeletonLoader from "@/modules/shared/components/AppSkeletonLoader.vue";
import { ConfirmService } from "@/modules/shared/services/confirm.service";
import { IdentityProvidersService } from "@/modules/identity-providers/services/identity-providers.service";
import {
  IdentityProvidersActionButton,
  IdentityProvidersInformationCard,
  IdentityProvidersOpenIdInformationCard,
  IdentityProvidersButtonInformationCard,
} from "../components/IdentityProvidersShow";

@Component({
  components: {
    AppButton,
    AppSkeletonLoader,
    IdentityProvidersActionButton,
    IdentityProvidersInformationCard,
    IdentityProvidersOpenIdInformationCard,
    IdentityProvidersButtonInformationCard,
  },
})
export default class IdentityProvidersShow extends Vue {
  @InjectService()
  private readonly $identityProvidersService!: IdentityProvidersService;

  @InjectService()
  private readonly $confirmService!: ConfirmService;

  private readonly identityProviderId = this.$route.params.identityProviderId as string;

  public identityProvider: Partial<FindIdentityProviderByIdResponse> | null = null;

  public isLoading = false;

  public get name(): string {
    return this.identityProvider?.name || "";
  }

  public get status(): boolean {
    return this.identityProvider?.active || false;
  }

  public get providerData(): ProviderData | undefined {
    return this.identityProvider?.providerData;
  }

  public async mounted(): Promise<void> {
    if (!this.identityProviderId) {
      await this.$router.push({
        name: "IdentityProvidersIndex",
      });
    }

    await this.fetchIdentityProvider();
  }

  public async disableIdentityProvider(): Promise<void> {
    try {
      const isConfirmed = await this.$confirmService.ask(
        "Disable Identity Provider?",
        "Are you sure you want to disable this Identity Provider?"
      );

      if (!isConfirmed) {
        return;
      }

      await this.$identityProvidersService.disable(+this.identityProviderId);
      await this.fetchIdentityProvider();

      this.$notify({
        type: "success",
        text: "Identity Provider was successfully disabled",
      });
    } catch {}
  }

  public async enableIdentityProvider(): Promise<void> {
    try {
      await this.$identityProvidersService.enable(+this.identityProviderId);
      await this.fetchIdentityProvider();

      this.$notify({
        type: "success",
        text: "Identity Provider was successfully enabled",
      });
    } catch {}
  }

  public async deleteIdentityProvider(): Promise<void> {
    try {
      const isConfirmed = await this.$confirmService.ask(
        "Delete Identity Provider?",
        "Are you sure you want to delete this Identity Provider?"
      );

      if (!isConfirmed) {
        return;
      }

      await this.$identityProvidersService.delete(+this.identityProviderId);

      this.$notify({
        type: "success",
        text: "IdentityProvider was successfully deleted",
      });
      await this.$router.replace({
        name: "IdentityProvidersIndex",
      });
    } catch {}
  }

  private async fetchIdentityProvider(): Promise<void> {
    try {
      this.isLoading = true;
      this.identityProvider = await this.$identityProvidersService.findById(+this.identityProviderId);
    } catch (error) {
      if (error instanceof AbortResponseError) {
        return;
      }
      await this.$router.push({
        name: "IdentityProvidersIndex",
      });
    } finally {
      this.isLoading = false;
    }
  }
}
